import React, { useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../store/store";
import { TimeDivider } from "./BlockRangeSelector";

const getBlockTimeRange = (blockNumber: number): string => {
  const blockNum = blockNumber;

  // Each block is 15 minutes, starting from 00:00
  const startMinutes = (blockNum - 1) * 15;
  const endMinutes = blockNum * 15;

  // Convert minutes to hours and minutes
  const startHour = Math.floor(startMinutes / 60);
  const startMin = startMinutes % 60;
  const endHour = Math.floor(endMinutes / 60);
  const endMin = endMinutes % 60;

  // Convert to 12-hour format
  const formatTo12Hour = (hour: number, minute: number): string => {
    const period = hour >= 12 ? "PM" : "AM";
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    return `${displayHour}:${minute.toString().padStart(2, "0")} ${period}`;
  };

  const startTime = formatTo12Hour(startHour, startMin);
  const endTime = formatTo12Hour(endHour, endMin);

  return `${startTime} - ${endTime}`;
};

const BlockRangeUtils = {
  // Get minimum and maximum from start and end blocks
  getMinMaxBlocks: (startBlock: number | null, endBlock: number | null) => {
    if (startBlock === null || endBlock === null)
      return { min: null, max: null };
    return {
      min: Math.min(startBlock, endBlock),
      max: Math.max(startBlock, endBlock),
    };
  },

  // Check if block is in range (exclusive of start and end)
  isBlockBetween: (
    block: number,
    startBlock: number | null,
    endBlock: number | null,
  ): boolean => {
    if (startBlock === null || endBlock === null) return false;
    const { min, max } = BlockRangeUtils.getMinMaxBlocks(startBlock, endBlock);
    return min !== null && max !== null && block > min && block < max;
  },

  // Check if block is in range (inclusive of start and end)
  isBlockInRange: (
    block: number,
    startBlock: number | null,
    endBlock: number | null,
  ): boolean => {
    if (startBlock === null || endBlock === null) return false;
    const { min, max } = BlockRangeUtils.getMinMaxBlocks(startBlock, endBlock);
    return min !== null && max !== null && block >= min && block <= max;
  },

  // Get all blocks in range (inclusive)
  getAllBlocksInRange: (
    startBlock: number | null,
    endBlock: number | null,
  ): number[] => {
    if (startBlock === null || endBlock === null) return [];
    const { min, max } = BlockRangeUtils.getMinMaxBlocks(startBlock, endBlock);
    if (min === null || max === null) return [];

    return Array.from({ length: max - min + 1 }, (_, i) => min + i);
  },

  // Get blocks count in range
  getBlocksCount: (
    startBlock: number | null,
    endBlock: number | null,
  ): number => {
    if (startBlock === null || endBlock === null) return 0;
    return Math.abs(endBlock - startBlock) + 1;
  },
};

const BlocksSelector = ({
  blocks,
  setSelectedOptions,
  setOpen,
  selectedOptions,
  setFinalRange,
  finalRange,
}: {
  blocks: number[];
  setSelectedOptions: (selected: number[]) => void;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  selectedOptions: {};
  setFinalRange: React.Dispatch<React.SetStateAction<{}>>;
  finalRange: {};
}) => {
  const BlockSuggestion = useSelector(
    (state: RootState) => state.BlockSuggestion,
  );
  const [selectedRange, setSelectedRange] = React.useState<{
    [key: string]: number[];
  }>(finalRange);

  // State for range selection
  const [internalStartBlock, setInternalStartBlock] = React.useState<
    number | null
  >(null);
  const [internalEndBlock, setInternalEndBlock] = React.useState<number | null>(
    null,
  );
  const [selectingEnd, setSelectingEnd] = React.useState(false);
  const [hoverBlock, setHoverBlock] = React.useState<number | null>(null);

  const handleRangeClick = useCallback(
    (rangeBlocks: { startBlock: number | null; endBlock: number | null }) => {
      const blocksInRange = BlockRangeUtils.getAllBlocksInRange(
        rangeBlocks.startBlock,
        rangeBlocks.endBlock,
      );

      function setMini() {
        let mini = Infinity;
        for (let i = 0; i < blocksInRange.length; i++) {
          if (blocksInRange[i] < mini) {
            mini = blocksInRange[i];
          }
        }
        return mini;
      }

      function setMaxi() {
        let maxi = -Infinity;
        for (let i = 0; i < blocksInRange.length; i++) {
          if (blocksInRange[i] > maxi) {
            maxi = blocksInRange[i];
          }
        }
        return maxi;
      }

      const newRangeMin = setMini();
      const newRangeMax = setMaxi();
      const newKey = `${newRangeMin}-${newRangeMax}`;

      // Check if the range already exists
      if (selectedRange[newKey]) {
        return;
      }

      // Handle overlapping ranges
      const handleOverlappingRanges = () => {
        const updatedRanges = { ...selectedRange };
        const overlappingKeys: string[] = [];

        // Find all overlapping ranges
        Object.keys(selectedRange).forEach((key) => {
          const existingBlocks = selectedRange[key];
          const hasOverlap = blocksInRange.some((block) =>
            existingBlocks.includes(block),
          );

          if (hasOverlap) {
            overlappingKeys.push(key);
          }
        });

        // If no overlaps, just add the new range
        if (overlappingKeys.length === 0) {
          updatedRanges[newKey] = blocksInRange;
          return updatedRanges;
        }

        // Process each overlapping range
        overlappingKeys.forEach((key) => {
          const existingBlocks = selectedRange[key];
          const existingMin = Math.min(...existingBlocks);
          const existingMax = Math.max(...existingBlocks);

          // Remove the overlapping range
          delete updatedRanges[key];

          // Check for partial overlap and create new ranges
          const beforeOverlap: number[] = [];
          const afterOverlap: number[] = [];

          // Find blocks before the new range
          for (let block = existingMin; block < newRangeMin; block++) {
            if (existingBlocks.includes(block)) {
              beforeOverlap.push(block);
            }
          }

          // Find blocks after the new range
          for (let block = newRangeMax + 1; block <= existingMax; block++) {
            if (existingBlocks.includes(block)) {
              afterOverlap.push(block);
            }
          }

          // Create new ranges if there are remaining blocks
          if (beforeOverlap.length > 0) {
            const beforeMin = Math.min(...beforeOverlap);
            const beforeMax = Math.max(...beforeOverlap);
            const beforeKey = `${beforeMin}-${beforeMax}`;
            updatedRanges[beforeKey] = beforeOverlap;
          }

          if (afterOverlap.length > 0) {
            const afterMin = Math.min(...afterOverlap);
            const afterMax = Math.max(...afterOverlap);
            const afterKey = `${afterMin}-${afterMax}`;
            updatedRanges[afterKey] = afterOverlap;
          }
        });

        // Add the new range
        updatedRanges[newKey] = blocksInRange;

        return updatedRanges;
      };

      setSelectedRange(handleOverlappingRanges());
    },
    [selectedRange],
  );

  // NEW: Handle individual block toggle
  const handleIndividualBlockToggle = useCallback(
    (block: number) => {
      const updatedRanges = { ...selectedRange };
      const blockKey = `${block}-${block}`;

      // If block is already selected individually, remove it
      if (updatedRanges[blockKey]) {
        delete updatedRanges[blockKey];
        setSelectedRange(updatedRanges);
        return;
      }

      // Check if block is part of any range
      let foundInRange = false;
      for (const key of Object.keys(selectedRange)) {
        const rangeBlocks = selectedRange[key];
        if (rangeBlocks.includes(block)) {
          foundInRange = true;
          const min = Math.min(...rangeBlocks);
          const max = Math.max(...rangeBlocks);

          // Remove the existing range
          delete updatedRanges[key];

          // Split the range, excluding the clicked block
          const beforeBlocks: number[] = [];
          const afterBlocks: number[] = [];

          for (let b = min; b < block; b++) {
            if (rangeBlocks.includes(b)) {
              beforeBlocks.push(b);
            }
          }

          for (let b = block + 1; b <= max; b++) {
            if (rangeBlocks.includes(b)) {
              afterBlocks.push(b);
            }
          }

          // Add remaining blocks as new ranges
          if (beforeBlocks.length > 0) {
            const beforeMin = Math.min(...beforeBlocks);
            const beforeMax = Math.max(...beforeBlocks);
            updatedRanges[`${beforeMin}-${beforeMax}`] = beforeBlocks;
          }

          if (afterBlocks.length > 0) {
            const afterMin = Math.min(...afterBlocks);
            const afterMax = Math.max(...afterBlocks);
            updatedRanges[`${afterMin}-${afterMax}`] = afterBlocks;
          }

          break;
        }
      }

      // If not found in any range, add as individual block
      if (!foundInRange) {
        updatedRanges[blockKey] = [block];
      }

      setSelectedRange(updatedRanges);
    },
    [selectedRange],
  );

  useEffect(() => {
    if (
      BlockSuggestion.startBlock !== null &&
      BlockSuggestion.endBlock !== null &&
      BlockSuggestion.isClicked
    ) {
      handleRangeClick({
        startBlock: BlockSuggestion.startBlock,
        endBlock: BlockSuggestion.endBlock,
      });
    }
  }, [BlockSuggestion, handleRangeClick]);

  function handleDiselectRange(block: number) {
    const selectedRangeCopy = { ...selectedRange };
    const selectedRangeKeys = Object.keys(selectedRange);

    for (let i = 0; i < selectedRangeKeys.length; i++) {
      const range = selectedRangeKeys[i].split("-");
      if (Number(range[0]) <= block && block <= Number(range[1])) {
        delete selectedRangeCopy[selectedRangeKeys[i]];
        setSelectedRange(selectedRangeCopy);
        return;
      }
    }
  }

  function handleApply() {
    const selectedBlocks: any[] = [];
    for (const key in selectedRange) {
      const range = key.split("-");
      const start = Number(range[0]);
      const end = Number(range[1]);
      for (let block = start; block <= end; block++) {
        selectedBlocks.push({ value: block, label: block });
      }
    }
    setFinalRange(selectedRange);
    setSelectedOptions(selectedBlocks);
    setOpen(false);
  }

  // UPDATED: Block click handler with double-click detection
  const handleBlockClick = (block: number, isDoubleClick: boolean = false) => {
    // Double-click = individual block toggle
    if (isDoubleClick) {
      handleIndividualBlockToggle(block);
      // Reset range selection state
      setInternalStartBlock(null);
      setInternalEndBlock(null);
      setSelectingEnd(false);
      setHoverBlock(null);
      return;
    }

    // Single click = range selection logic
    const { inSelectedRange } = getBlockInRangeStatus(block);

    // If clicking a finalized block, deselect that range
    if (inSelectedRange && !isBlockInCurrentSelection(block)) {
      handleDiselectRange(block);
      return;
    }

    // If selecting end block, finalize the range
    if (internalStartBlock !== null && selectingEnd) {
      // Check if clicking the same start block while selecting end
      if (block === internalStartBlock) {
        // Deselect everything
        setInternalStartBlock(null);
        setInternalEndBlock(null);
        setSelectingEnd(false);
        setHoverBlock(null);
        return;
      }

      // Finalize the range
      const rangeStart = Math.min(internalStartBlock, block);
      const rangeEnd = Math.max(internalStartBlock, block);

      handleRangeClick({
        startBlock: rangeStart,
        endBlock: rangeEnd,
      });

      setInternalStartBlock(null);
      setInternalEndBlock(null);
      setSelectingEnd(false);
      setHoverBlock(null);
    }
    // If no start block or starting a new selection
    else if (internalStartBlock === null) {
      setInternalStartBlock(block);
      setInternalEndBlock(block);
      setSelectingEnd(true);
      setHoverBlock(null);
    }
    // If both blocks are already selected (complete range), start new selection
    else if (
      internalStartBlock !== null &&
      internalEndBlock !== null &&
      !selectingEnd
    ) {
      setInternalStartBlock(block);
      setInternalEndBlock(block);
      setSelectingEnd(true);
      setHoverBlock(null);
    }
  };

  const handleBlockHover = (block: number) => {
    if (internalStartBlock !== null && selectingEnd) {
      setHoverBlock(block);
    }
  };

  const handleBlockLeave = () => {
    if (selectingEnd) {
      setHoverBlock(null);
    }
  };

  // Helper: Check if block is in any finalized selected range
  const getBlockInRangeStatus = (block: number) => {
    for (const rangeKey of Object.keys(selectedRange)) {
      const rangeBlocks = selectedRange[rangeKey];
      if (rangeBlocks.includes(block)) {
        const minBlock = Math.min(...rangeBlocks);
        const maxBlock = Math.max(...rangeBlocks);
        const isFirstOrLast = block === minBlock || block === maxBlock;
        const isSingleBlock = minBlock === maxBlock;
        return { inSelectedRange: true, isFirstOrLast, isSingleBlock };
      }
    }
    return {
      inSelectedRange: false,
      isFirstOrLast: false,
      isSingleBlock: false,
    };
  };

  // Helper: Check if block is in current selection (including hover preview)
  const isBlockInCurrentSelection = (block: number): boolean => {
    // Show hover preview when selecting end
    if (internalStartBlock !== null && selectingEnd && hoverBlock !== null) {
      const previewStart = Math.min(internalStartBlock, hoverBlock);
      const previewEnd = Math.max(internalStartBlock, hoverBlock);
      return block >= previewStart && block <= previewEnd;
    }

    // Show finalized internal selection
    if (
      internalStartBlock !== null &&
      internalEndBlock !== null &&
      !selectingEnd
    ) {
      return (
        block >= Math.min(internalStartBlock, internalEndBlock) &&
        block <= Math.max(internalStartBlock, internalEndBlock)
      );
    }

    // Show just start block
    if (internalStartBlock !== null && block === internalStartBlock) {
      return true;
    }

    return false;
  };

  // Helper: Check if block is edge of current selection
  const isBlockEdgeOfCurrentSelection = (block: number): boolean => {
    // During hover preview
    if (internalStartBlock !== null && selectingEnd && hoverBlock !== null) {
      return block === internalStartBlock || block === hoverBlock;
    }

    // Finalized selection
    if (
      internalStartBlock !== null &&
      internalEndBlock !== null &&
      !selectingEnd
    ) {
      return block === internalStartBlock || block === internalEndBlock;
    }

    // Just start block
    return internalStartBlock !== null && block === internalStartBlock;
  };

  return (
    <>
      <div className="grid grid-cols-3 gap-2 p-5">
        {TimeDivider.map((time, index) => {
          return (
            <div
              key={index}
              className={`text-xs col-span-3 p-2 rounded-lg ${index % 2 === 1 ? "bg-gray-50" : ""
                }`}
            >
              <div className="flex items-center justify-between">
                <div className="font-medium text-gray-700">
                  <span>{time.name}</span>
                  <span> - {time.range}</span>
                </div>
              </div>

              <div className="flex flex-wrap gap-1 mt-2">
                {blocks.map((block, blockIndex) => {
                  if (time.startBlock <= block && block <= time.endBlock) {
                    return (
                      <BlocksSelectorBtn
                        key={blockIndex}
                        index={blockIndex}
                        block={block}
                        selectedRange={selectedRange}
                        handleBlockClick={handleBlockClick}
                        handleBlockHover={handleBlockHover}
                        handleBlockLeave={handleBlockLeave}
                        isBlockInCurrentSelection={isBlockInCurrentSelection}
                        isBlockEdgeOfCurrentSelection={
                          isBlockEdgeOfCurrentSelection
                        }
                        getBlockInRangeStatus={getBlockInRangeStatus}
                        BlockSuggestion={BlockSuggestion}
                      />
                    );
                  } else {
                    return null;
                  }
                })}
              </div>
            </div>
          );
        })}
      </div>

      <div className="border-t p-4 px-5 w-full">
        <div className="flex items-center justify-between">
          <div>
            <button
              onClick={() => {
                setSelectedRange({});
                setInternalStartBlock(null);
                setInternalEndBlock(null);
                setSelectingEnd(false);
                setHoverBlock(null);
              }}
              className="hover:text-red-500 font-medium text-[0.8rem] px-5 py-2 rounded-lg hover:border-red-500 transition-colors ease-in-out border duration-100"
            >
              Clear All
            </button>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={() => {
                setOpen(false);
              }}
              className="px-5 py-2 rounded-lg border"
            >
              Cancel
            </button>
            <button
              onClick={() => {
                handleApply();
              }}
              className="bg-primary text-white px-5 py-2 rounded-lg hover:bg-primary/90 transition-colors ease-in-out duration-100"
            >
              Apply
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

function BlocksSelectorBtn({
  index,
  block,
  selectedRange,
  handleBlockClick,
  handleBlockHover,
  handleBlockLeave,
  isBlockInCurrentSelection,
  isBlockEdgeOfCurrentSelection,
  getBlockInRangeStatus,
  BlockSuggestion,
}: {
  index: number;
  block: number;
  selectedRange: { [key: string]: number[] };
  handleBlockClick: (block: number, isDoubleClick?: boolean) => void;
  handleBlockHover: (block: number) => void;
  handleBlockLeave: () => void;
  isBlockInCurrentSelection: (block: number) => boolean;
  isBlockEdgeOfCurrentSelection: (block: number) => boolean;
  getBlockInRangeStatus: (block: number) => {
    inSelectedRange: boolean;
    isFirstOrLast: boolean;
    isSingleBlock: boolean;
  };
  BlockSuggestion: any;
}) {
  const [btnIndex, setBtnIndex] = React.useState<number | null>(null);

  // Check if block is in suggestion preview
  const isInSuggestionPreview = () => {
    if (
      BlockSuggestion.startBlock &&
      BlockSuggestion.endBlock &&
      !BlockSuggestion.isClicked
    ) {
      return (
        block >= BlockSuggestion.startBlock && block <= BlockSuggestion.endBlock
      );
    }
    return false;
  };

  const isSuggestionEdge = () => {
    if (
      BlockSuggestion.startBlock &&
      BlockSuggestion.endBlock &&
      !BlockSuggestion.isClicked
    ) {
      return (
        block === BlockSuggestion.startBlock ||
        block === BlockSuggestion.endBlock
      );
    }
    return false;
  };

  return (
    <div key={index} className="relative">
      <button
        onClick={() => handleBlockClick(block, false)}
        onDoubleClick={() => handleBlockClick(block, true)}
        onMouseEnter={() => {
          handleBlockHover(block);
          setBtnIndex(index);
        }}
        onMouseLeave={() => {
          handleBlockLeave();
          setBtnIndex(null);
        }}
        className={`h-8 w-8 text-xs rounded-lg transition-colors ease-in-out duration-100 ${(() => {
          const { inSelectedRange, isFirstOrLast, isSingleBlock } =
            getBlockInRangeStatus(block);

          // Priority 1: Current selection (being created) - like DateRangeSelector
          if (isBlockInCurrentSelection(block)) {
            return isBlockEdgeOfCurrentSelection(block)
              ? "bg-[#F1935C] text-white font-semibold"
              : "bg-[#ffe4cd] text-[#F1935C]";
          }

          // Priority 2: Suggestion hover preview (blue to differentiate)
          if (isInSuggestionPreview()) {
            return isSuggestionEdge()
              ? "bg-blue-500 text-white font-semibold"
              : "bg-blue-100 text-blue-600";
          }

          // Priority 3: Finalized selected ranges
          if (inSelectedRange) {
            if (isSingleBlock) {
              return "bg-primary text-white";
            }
            return isFirstOrLast
              ? "bg-primary text-white"
              : "bg-primary/10 text-primary";
          }

          // Priority 4: Default state
          return "text-[#76797c] hover:border-primary hover:border";
        })()}`}
        title="Single-click for range, double-click for individual selection"
      >
        {block}
      </button>
      {btnIndex === index && <Tooltip content={getBlockTimeRange(block)} />}
    </div>
  );
}

export function Tooltip({ content }: { content: string }) {
  return (
    <div className="absolute top-[-45px] text-nowrap left-1/2 transform transition-all ease-linear duration-150 -translate-x-1/2 z-[99999] inline-block px-3 py-2 font-medium text-xs border text-primary bg-white rounded-lg shadow-lg">
      {content}
      <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-[6px] border-r-[6px] border-t-[6px] border-l-transparent border-r-transparent border-t-gray-100"></div>
    </div>
  );
}

export default BlocksSelector;
